import { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  History,
  Loader2,
  AlertTriangle,
  ArrowRight,
  Undo2,
  CheckCircle2,
} from 'lucide-react';

/**
 * Phase 3.1 — Cashflow Apply Audit Viewer.
 * Phase 3.2 — Undo Last Apply (latest-row, 5-min safety window).
 *
 * Read-only history of previously applied planner moves. Collapsed by
 * default; fetches lazily on first expand. Shows date, action count,
 * total moved, before/after personal+business+savings, and warnings.
 *
 * The most-recent row (and only that row) gets an "Undo last apply"
 * affordance when the server marks it `undo_eligible`. Eligibility is
 * computed server-side so client clock skew can't extend the window.
 *
 * Privacy: backend GET /api/cashflow/apply-audit filters strictly on the
 * authenticated user's id — no cross-user data is ever shown.
 */

const fmt = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

function BeforeAfterRow({ label, before, after }) {
  const beforeNum = Number(before || 0);
  const afterNum = Number(after || 0);
  if (beforeNum === 0 && afterNum === 0) return null;
  const delta = afterNum - beforeNum;
  const deltaColor =
    delta > 0
      ? 'text-emerald-300'
      : delta < 0
      ? 'text-rose-300'
      : 'text-gray-400';
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="text-gray-400">{label}</span>
      <span className="font-mono text-gray-300">
        {fmt(beforeNum)} <ArrowRight className="inline w-3 h-3 mx-0.5 text-gray-500" /> {fmt(afterNum)}
        <span className={`ml-2 ${deltaColor}`}>
          {delta >= 0 ? '+' : ''}
          {fmt(delta)}
        </span>
      </span>
    </div>
  );
}

function UndoConfirmPanel({ preview, onConfirm, onCancel, committing }) {
  const ra = preview.reverse_actual_deltas || {};
  const before = preview.before_actual_now || {};
  const after = preview.projected_actual_after_undo || {};
  return (
    <div
      className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
      data-testid="cashflow-undo-confirm-panel"
    >
      <div className="flex items-center gap-1.5 text-xs text-amber-200">
        <AlertTriangle className="w-3.5 h-3.5" />
        <strong>Confirm undo</strong>
        <span className="text-amber-300/80">— this will reverse the move below.</span>
      </div>
      <div className="space-y-0.5">
        <BeforeAfterRow label="Personal" before={before.personal} after={after.personal} />
        <BeforeAfterRow label="Business" before={before.business} after={after.business} />
        <BeforeAfterRow label="Savings" before={before.savings} after={after.savings} />
      </div>
      <div className="text-[11px] text-amber-200/80">
        Net reversal:&nbsp;
        <span className="font-mono">
          P {ra.personal > 0 ? '+' : ''}{fmt(ra.personal || 0)} ·{' '}
          B {ra.business > 0 ? '+' : ''}{fmt(ra.business || 0)} ·{' '}
          S {ra.savings > 0 ? '+' : ''}{fmt(ra.savings || 0)}
        </span>
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={committing}
          className="px-2.5 py-1 rounded-md text-[11px] text-amber-100 hover:bg-amber-500/20 disabled:opacity-60"
          data-testid="cashflow-undo-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={committing}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-400 hover:bg-amber-300 text-slate-900 text-[11px] font-semibold disabled:opacity-60"
          data-testid="cashflow-undo-confirm"
        >
          {committing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Undo2 className="w-3 h-3" />
          )}
          Confirm undo
        </button>
      </div>
    </div>
  );
}

function AuditRow({ row, isLatest, onUndone }) {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);

  const undoneAt = row.undoneAt;
  const undoEligible = isLatest && row.undo_eligible && !undoneAt;

  const startPreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/cashflow/apply-audit/undo-latest`, { dryRun: true });
      setPreview(res.data);
    } catch (err) {
      console.error('Undo preview failed:', err);
      setError(err?.response?.data?.detail || 'Unable to preview undo.');
      setPreviewing(false);
    }
  };

  const cancelPreview = () => {
    setPreviewing(false);
    setPreview(null);
    setError(null);
  };

  const confirmUndo = async () => {
    setCommitting(true);
    setError(null);
    try {
      await axios.post(`${API}/cashflow/apply-audit/undo-latest`, { dryRun: false });
      setPreviewing(false);
      setPreview(null);
      onUndone?.();
    } catch (err) {
      console.error('Undo commit failed:', err);
      setError(err?.response?.data?.detail || 'Unable to undo this apply.');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div
      className={`rounded-xl border p-3 space-y-2 ${
        undoneAt
          ? 'border-slate-700/40 bg-slate-900/40 opacity-75'
          : 'border-slate-700/60 bg-slate-900/70'
      }`}
      data-testid="cashflow-audit-row"
      data-undone={undoneAt ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold text-white" data-testid="cashflow-audit-applied-at">
          {fmtDate(row.applied_at)}
        </p>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="px-2 py-0.5 rounded-full bg-slate-800 text-gray-300 border border-slate-700/80">
            {row.action_count} action{row.action_count === 1 ? '' : 's'}
          </span>
          <span
            className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-semibold"
            data-testid="cashflow-audit-total-moved"
          >
            Moved {fmt(row.total_moved)}
          </span>
          {undoneAt && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700/60 text-gray-200 border border-slate-600/60"
              data-testid="cashflow-audit-undone-badge"
              title={`Undone ${fmtDate(undoneAt)}`}
            >
              <CheckCircle2 className="w-3 h-3" />
              Undone
            </span>
          )}
        </div>
      </div>

      {row.actions?.length > 0 && (
        <ul className="text-[11px] text-gray-300 space-y-0.5 list-disc pl-4">
          {row.actions.map((a) => (
            <li key={`${row.applied_at}-${a.step}`}>
              <span className="text-gray-200">{a.label || a.actionType}</span>
              {a.amount > 0 && (
                <span className="text-gray-500"> — {fmt(a.amount)}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-dashed border-slate-700/60 pt-2 space-y-0.5">
        <BeforeAfterRow
          label="Personal"
          before={row.before_actual?.personal}
          after={row.after_actual?.personal}
        />
        <BeforeAfterRow
          label="Business"
          before={row.before_actual?.business}
          after={row.after_actual?.business}
        />
        <BeforeAfterRow
          label="Savings"
          before={row.before_actual?.savings}
          after={row.after_actual?.savings}
        />
      </div>

      {row.warnings?.length > 0 && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-300/90">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <ul className="space-y-0.5">
            {row.warnings.map((w) => (
              <li key={`${row.applied_at}-${w}`}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {undoEligible && !previewing && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] text-gray-400">
            Undo window: {Math.max(0, Math.floor(row.seconds_until_undo_expires / 60))}m{' '}
            {Math.max(0, Math.floor(row.seconds_until_undo_expires % 60))}s remaining
          </span>
          <button
            type="button"
            onClick={startPreview}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-amber-300 text-[11px] font-semibold border border-amber-500/30"
            data-testid="cashflow-undo-latest-btn"
            title="Reverse this apply (within 5 minutes)"
          >
            <Undo2 className="w-3 h-3" />
            Undo last apply
          </button>
        </div>
      )}

      {previewing && preview && (
        <UndoConfirmPanel
          preview={preview}
          onConfirm={confirmUndo}
          onCancel={cancelPreview}
          committing={committing}
        />
      )}

      {error && (
        <div
          className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-2 py-1"
          data-testid="cashflow-undo-error"
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default function CashflowApplyHistory() {
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/cashflow/apply-audit`, {
        params: { limit: 25 },
      });
      setItems(res.data?.items || []);
      setLoaded(true);
    } catch (err) {
      console.error('Cashflow apply-audit failed:', err);
      setError('Failed to load applied moves history.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Lazy-fetch on first expand only.
  const handleToggle = (e) => {
    if (e.currentTarget.open && !loaded && !loading) {
      fetchAudit();
    }
  };

  return (
    <details
      className="rounded-xl border border-slate-700/60 bg-slate-900/50 overflow-hidden"
      onToggle={handleToggle}
      data-testid="cashflow-apply-history"
    >
      <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-gray-300" />
          <p className="text-sm font-semibold text-white">Applied Moves History</p>
        </div>
        <span className="text-[11px] text-gray-400">
          {loaded ? `${items.length} entr${items.length === 1 ? 'y' : 'ies'}` : 'Click to load'}
        </span>
      </summary>

      <div className="px-4 pb-4 pt-1 space-y-2">
        {loading && (
          <div
            className="flex items-center gap-2 text-xs text-gray-400 py-3"
            data-testid="cashflow-audit-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading history…
          </div>
        )}

        {error && (
          <div className="flex items-center justify-between gap-2 rounded-md bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-200">
            <span>{error}</span>
            <button
              type="button"
              onClick={fetchAudit}
              className="px-2 py-1 rounded bg-white text-slate-900 text-[11px] font-semibold hover:bg-gray-100"
              data-testid="cashflow-audit-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {loaded && !loading && !error && items.length === 0 && (
          <p
            className="text-xs text-gray-400 py-3"
            data-testid="cashflow-audit-empty"
          >
            No applied moves yet. Once you commit moves from “Apply these moves”, they’ll show up here.
          </p>
        )}

        {loaded && !error && items.length > 0 && (
          <div className="space-y-2" data-testid="cashflow-audit-list">
            {items.map((row, idx) => (
              <AuditRow
                key={row.applied_at}
                row={row}
                isLatest={idx === 0}
                onUndone={fetchAudit}
              />
            ))}
          </div>
        )}
      </div>
    </details>
  );
}
